/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import { useCallback, useMemo } from 'react';
import { useUtils } from '~/shared/hooks/useUtils';
import { useFrame } from '~/shared/utils/frame';
import useBasket from './useBasket';
import useCustomer from './useCustomer';
import { useLocalBasketState } from '~/features/basket/hooks/useLocalBasketState';
import { useQuery } from '@tanstack/react-query';
import { useCommerceAPI } from '../contexts';
import { useMarket } from '~/shared/utils';
import { Product } from '~/lib/data-contract/salesforce';

/**
 * Joins basket and customer hooks into a single hook for initializing their states
 * when the app loads on the client-side. Should only be use at top-level of app.
 * @returns {Object} - customer and basket objects
 */
const useProducts = (opts: { currency?: string } = {}) => {
    const { currency } = opts;
    const {
        data: { market },
    } = useFrame();

    const currencyToUse = currency || market.currency?.currency;

    const { isoCulture } = useMarket();
    const { shopperProducts } = useCommerceAPI();
    const { customerId } = useCustomer();
    const {
        productTotal,
        orderTotal,
        c_totalDiscountExclShipping,
        c_totalMemberDiscount,
        c_subTotalRetailPrices,
        shippingTotal,
        productItems,
        basketId,
    } = useBasket({ currency: currencyToUse });
    const { membership } = useLocalBasketState();
    const { convertPriceToLocale } = useUtils();

    const freeShippingThreshold = market?.minimumBasketTotalForFreeShipping;

    const handlers = useMemo(
        () => ({
            getTotalPrice: () => {
                return convertPriceToLocale(productTotal);
            },

            getPrices: () => {
                const discountPrice = c_totalDiscountExclShipping ?? 0;
                const memberDiscount = c_totalMemberDiscount ?? 0;
                const totalPrice = c_subTotalRetailPrices ?? 0;
                const memberPrice = orderTotal && orderTotal - memberDiscount;
                const customerPrice = membership ? memberPrice : orderTotal;
                const priceMemberSelected = discountPrice + memberDiscount;

                return {
                    discountPrice,
                    memberDiscount,
                    totalPrice,
                    memberPrice,
                    customerPrice: convertPriceToLocale(customerPrice),
                    priceMemberSelected,
                };
            },

            getHasFreeShipping: () => shippingTotal === 0,

            getAmountToFreeShipping: () => {
                if (
                    shippingTotal === 0 ||
                    freeShippingThreshold === undefined ||
                    orderTotal === undefined
                ) {
                    return;
                }

                const fallbackTotal = orderTotal - (shippingTotal ?? 0);
                const productsTotal = productTotal ?? fallbackTotal;

                return convertPriceToLocale(Math.max(0, freeShippingThreshold - productsTotal));
            },
        }),
        [customerId, orderTotal],
    );

    const productIds = useMemo(
        () => (productItems?.map((item) => item.productId) ?? []).join(','),
        [productItems],
    );

    const { data: products, isFetched: isProductDataFetched } = useQuery({
        queryFn: async () => {
            const products = await shopperProducts?.getProducts({
                parameters: {
                    ids: productIds,
                    locale: isoCulture,
                    currency: currencyToUse,
                    allImages: true,
                },
            });

            return products?.data ?? [];
        },
        queryKey: [basketId, productIds],
        enabled: !!basketId && !!productIds.length,
    });

    /**
     * 'productsInBasket' contains merged information of the basket line item and the product details.
     * This is useful for presenting the line items with relevant product information.
     */
    const productsInBasket = useMemo(
        () =>
            (productItems?.map((item) => {
                const matchedIndex =
                    products?.findIndex((product) => product.id === item.productId) ?? -1;

                if (matchedIndex < 0) return item;

                return {
                    index: matchedIndex,
                    ...products?.[matchedIndex],
                    ...item,
                    // Always take the countdown from the basket, even when it has none. Otherwise the
                    // product's countdown is kept, which can be cached and already over.
                    c_promotionCountdown: item.c_promotionCountdown,
                };
            }) ?? []) as Product[],
        [products, productItems],
    );

    const accumulatedQuantity = useMemo(() => {
        return productsInBasket?.reduce((total, curr) => total + curr.quantity!, 0) || 0;
    }, [productsInBasket]);

    const getProductsByShipment = useCallback(
        (shipmentId: string) =>
            productsInBasket?.filter((product) => product.shipmentId === shipmentId),
        [productsInBasket],
    );

    return {
        handlers,
        products: productsInBasket,
        accumulatedQuantity,
        getProductsByShipment,
        isProductDataReady: !productIds.length || isProductDataFetched,
    };
};

export default useProducts;
